import type { AlbumCover, VisibleCoverSlot } from "./AlbumCoverTypes";
import { clampValue, getWrappedIndex } from "./HeroMusicUtils";

type CarouselStatus = "idle" | "dragging";

type CarouselState = {
  activeIndex: number;
  dragOffset: number;
  renderedRotationIndex: number;
  status: CarouselStatus;
  targetRotationIndex: number;
};

export type CarouselAction =
  | { type: "dragBy"; deltaIndex: number }
  | { type: "endDrag"; itemCount: number; releaseVelocity: number }
  | { type: "moveBy"; deltaIndex: number; itemCount: number }
  | { type: "selectCoverIndex"; coverIndex: number; itemCount: number }
  | { type: "startDragAt"; renderedRotationIndex: number }
  | { type: "syncActiveIndex"; activeIndex: number; itemCount: number };

export type DragVelocitySample = {
  deltaIndex: number;
  elapsedSeconds: number;
  timestamp: number;
};

type CarouselStoreListener = () => void;

export const DRAG_START_DISTANCE_THRESHOLD = 8;
export const DRAG_VELOCITY_FILTER_ALPHA = 0.45;
export const DRAG_VELOCITY_MAX_SAMPLE_INTERVAL_SECONDS = 0.08;

const DRAG_COMMIT_SLOT_THRESHOLD = 0.18;
const DRAG_RELEASE_DAMPING_RATE = 4;
const DRAG_RELEASE_MAX_SLOT_DELTA = 4;
const DRAG_RELEASE_MIN_PROJECTED_SLOT_TRAVEL = 0.34;
const DRAG_RELEASE_MIN_VELOCITY_FOR_INERTIA = 0.5;
const DRAG_RELEASE_VELOCITY_EPSILON = 0.08;
const DRAG_VELOCITY_SAMPLE_WINDOW_MS = 120;

export function createInitialCarouselState(activeIndex: number) {
  return {
    activeIndex,
    dragOffset: 0,
    renderedRotationIndex: activeIndex,
    status: "idle",
    targetRotationIndex: activeIndex,
  } satisfies CarouselState;
}

export function reduceCarouselState(state: CarouselState, action: CarouselAction) {
  switch (action.type) {
    case "dragBy": {
      const nextDragOffset = state.dragOffset + action.deltaIndex;

      return {
        ...state,
        dragOffset: nextDragOffset,
        renderedRotationIndex: state.targetRotationIndex + nextDragOffset,
        status: "dragging",
      } satisfies CarouselState;
    }

    case "endDrag": {
      if (action.itemCount <= 0) {
        return {
          ...state,
          dragOffset: 0,
          status: "idle",
        } satisfies CarouselState;
      }

      const directionalRotationDelta = getDragReleaseRotationDelta(
        state.dragOffset,
        action.releaseVelocity,
        action.itemCount,
      );
      const nextTargetRotationIndex = state.targetRotationIndex + directionalRotationDelta;

      return {
        activeIndex: getWrappedIndex(state.activeIndex + directionalRotationDelta, action.itemCount),
        dragOffset: 0,
        renderedRotationIndex: nextTargetRotationIndex,
        status: "idle",
        targetRotationIndex: nextTargetRotationIndex,
      } satisfies CarouselState;
    }

    case "moveBy": {
      if (action.itemCount <= 0) return state;

      const nextTargetRotationIndex = state.targetRotationIndex + action.deltaIndex;

      return {
        activeIndex: getWrappedIndex(state.activeIndex + action.deltaIndex, action.itemCount),
        dragOffset: 0,
        renderedRotationIndex: nextTargetRotationIndex,
        status: "idle",
        targetRotationIndex: nextTargetRotationIndex,
      } satisfies CarouselState;
    }

    case "selectCoverIndex": {
      if (action.itemCount <= 0) return state;

      const nextActiveIndex = getWrappedIndex(action.coverIndex, action.itemCount);
      const slotOffset = getShortestIndexOffset(state.activeIndex, nextActiveIndex, action.itemCount);

      if (slotOffset === 0 && state.dragOffset === 0) return state;

      const nextTargetRotationIndex = state.targetRotationIndex + slotOffset;

      return {
        activeIndex: nextActiveIndex,
        dragOffset: 0,
        renderedRotationIndex: nextTargetRotationIndex,
        status: "idle",
        targetRotationIndex: nextTargetRotationIndex,
      } satisfies CarouselState;
    }

    case "startDragAt": {
      const nextDragOffset = action.renderedRotationIndex - state.targetRotationIndex;

      return {
        ...state,
        dragOffset: nextDragOffset,
        renderedRotationIndex: action.renderedRotationIndex,
        status: "dragging",
      } satisfies CarouselState;
    }

    case "syncActiveIndex": {
      if (action.itemCount <= 0) return state;

      const currentActiveIndex = getWrappedIndex(state.activeIndex, action.itemCount);
      const nextActiveIndex = getWrappedIndex(action.activeIndex, action.itemCount);
      const activeOffset = getShortestIndexOffset(currentActiveIndex, nextActiveIndex, action.itemCount);

      if (activeOffset === 0 && state.dragOffset === 0 && state.activeIndex === currentActiveIndex) {
        return state;
      }

      const nextTargetRotationIndex = state.targetRotationIndex + activeOffset;

      return {
        activeIndex: nextActiveIndex,
        dragOffset: 0,
        renderedRotationIndex: nextTargetRotationIndex,
        status: "idle",
        targetRotationIndex: nextTargetRotationIndex,
      } satisfies CarouselState;
    }
  }
}

export function createCarouselStore(initialState: CarouselState) {
  let state = initialState;
  const listeners = new Set<CarouselStoreListener>();

  function emit() {
    listeners.forEach(listener => listener());
  }

  return {
    dispatch(action: CarouselAction) {
      const previousState = state;
      const nextState = reduceCarouselState(previousState, action);

      if (nextState !== previousState) {
        state = nextState;
        emit();
      }

      return { previousState, nextState };
    },
    getSnapshot() {
      return state;
    },
    subscribe(listener: CarouselStoreListener) {
      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export function getVisibleCoverSlots(
  covers: AlbumCover[],
  activeCoverIndex: number,
  rotationIndex: number,
  count: number,
) {
  if (covers.length === 0 || count <= 0) return [];

  const activeSlotIndex = getWrappedIndex(rotationIndex, count);

  return Array.from({ length: count }, (_, slotIndex) => {
    const coverOffset = getSignedSlotOffset(activeSlotIndex, slotIndex, count);
    const coverIndex = getWrappedIndex(activeCoverIndex + coverOffset, covers.length);

    return {
      cover: covers[coverIndex],
      coverIndex,
      slotId: slotIndex,
    };
  }).filter((slot): slot is VisibleCoverSlot => Boolean(slot.cover));
}

export function decayDragVelocity(velocity: number, elapsedSeconds: number) {
  const speed = Math.abs(velocity);

  if (speed <= DRAG_RELEASE_VELOCITY_EPSILON || elapsedSeconds <= 0) return velocity;

  return velocity * Math.exp(-DRAG_RELEASE_DAMPING_RATE * elapsedSeconds);
}

export function getRecentDragVelocitySamples(samples: DragVelocitySample[], timestamp: number) {
  const sampleWindowStart = timestamp - DRAG_VELOCITY_SAMPLE_WINDOW_MS;

  return samples.filter(sample => sample.timestamp >= sampleWindowStart);
}

export function getDragVelocityFromSamples(samples: DragVelocitySample[], timestamp: number) {
  const recentSamples = getRecentDragVelocitySamples(samples, timestamp);
  const totalElapsedSeconds = recentSamples.reduce((total, sample) => total + sample.elapsedSeconds, 0);

  if (totalElapsedSeconds <= 0) return 0;

  const totalDeltaIndex = recentSamples.reduce((total, sample) => total + sample.deltaIndex, 0);

  return totalDeltaIndex / totalElapsedSeconds;
}

function getDragReleaseRotationDelta(dragOffset: number, releaseVelocity: number, itemCount: number) {
  const projectedDragOffset = dragOffset + getProjectedDragSlotTravel(releaseVelocity);
  const roundedRotationDelta = Math.round(projectedDragOffset);
  const directionalRotationDelta =
    roundedRotationDelta === 0 && Math.abs(projectedDragOffset) >= DRAG_COMMIT_SLOT_THRESHOLD
      ? Math.sign(projectedDragOffset)
      : roundedRotationDelta;
  const maxReleaseDelta = clampValue(itemCount - 1, 0, DRAG_RELEASE_MAX_SLOT_DELTA);

  return clampValue(directionalRotationDelta, -maxReleaseDelta, maxReleaseDelta);
}

function getProjectedDragSlotTravel(releaseVelocity: number) {
  const speed = Math.abs(releaseVelocity);

  if (speed <= DRAG_RELEASE_VELOCITY_EPSILON) return 0;

  const projectedTravel =
    Math.sign(releaseVelocity) * ((speed - DRAG_RELEASE_VELOCITY_EPSILON) / DRAG_RELEASE_DAMPING_RATE);

  if (speed < DRAG_RELEASE_MIN_VELOCITY_FOR_INERTIA) return projectedTravel;

  return Math.sign(releaseVelocity) * Math.max(Math.abs(projectedTravel), DRAG_RELEASE_MIN_PROJECTED_SLOT_TRAVEL);
}

function getSignedSlotOffset(activeSlotIndex: number, slotIndex: number, count: number) {
  let offset = slotIndex - activeSlotIndex;

  while (offset > count / 2) offset -= count;
  while (offset <= -count / 2) offset += count;

  return offset;
}

function getShortestIndexOffset(fromIndex: number, toIndex: number, count: number) {
  const forwardOffset = getWrappedIndex(toIndex - fromIndex, count);

  if (forwardOffset > count / 2) {
    return forwardOffset - count;
  }

  return forwardOffset;
}
